import { Button, Popover, PopoverList } from "@xola/ui-kit";
import pluralize from "pluralize";
import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import { ProductObject, SelectionState } from "types";
import { ProductGroupType, ProductSelector } from "@/components/Common/ProductSelector";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { FilterMenuButton } from "@/components/Dashboard/Filters/FilterMenuButton";
import { useVisibility } from "@/hooks/useVisibility";

export interface TranslationsApiFilters {
    seller: ID;
    locale: string;
    tag: string[];
    ancestors: ID[];
}

interface TranslationsFilterProps {
    filters: TranslationsApiFilters;
    setFilters: Dispatch<SetStateAction<TranslationsApiFilters>>;
}

interface FilterProps<T> {
    value: T[];
    onChange: (value: T[]) => void;
}

export const TranslationsFilter = ({ filters, setFilters }: TranslationsFilterProps) => {
    const handleFilterChange =
        <Key extends keyof TranslationsApiFilters>(filterKey: Key) =>
        (value: TranslationsApiFilters[Key]) => {
            setFilters((previousFilters) => ({ ...previousFilters, [filterKey]: value }));
        };

    return (
        <div className="flex gap-x-4">
            <TranslationStateFilter value={filters.tag} onChange={handleFilterChange("tag")} />
            <TranslationProductFilter value={filters.ancestors} onChange={handleFilterChange("ancestors")} />
        </div>
    );
};

interface Option {
    label: string;
    value: string;
}

const options: Option[] = [
    { label: "All Content", value: "all" },
    { label: "Manually translated", value: "manual" },
    { label: "Untranslated", value: "untranslated" },
    { label: "Original content modified", value: "fuzzy" },
];

const TranslationStateFilter = ({ value, onChange }: FilterProps<string>) => {
    const [showPopover, { toggle: togglePopover }] = useVisibility(false);
    const selectedOption = value.length > 0 ? value[0] : "all";
    const buttonText = options.find((option) => option.value === selectedOption)!.label;

    const handleChange = (tag: string) => {
        onChange(tag === "all" ? [] : [tag]);
        togglePopover();
    };

    return (
        <div className="relative">
            <PopoverList
                className="w-50"
                placement="bottom"
                offset={[0, 18]}
                visible={showPopover}
                onClickOutside={togglePopover}
            >
                <FilterMenuButton isDropdown className="h-10 w-50" text={buttonText} onClick={togglePopover} />
                {options.map((option) => (
                    <PopoverList.Item
                        key={option}
                        name={option}
                        isActive={option.value === selectedOption}
                        onClickItem={() => handleChange(option.value)}
                    >
                        {option.label}
                    </PopoverList.Item>
                ))}
            </PopoverList>
        </div>
    );
};

const TranslationProductFilter = ({ value, onChange }: FilterProps<ID>) => {
    const [showPopover, { toggle: togglePopover }] = useVisibility(false);

    const buttonText = useMemo(() => {
        const texts = [];
        const selectedProducts = value.filter((productId) => productId !== "global").length;
        if (selectedProducts > 0) {
            texts.push(`${selectedProducts} ${pluralize("Product", selectedProducts)}`);
        }

        if (value.includes("global")) {
            texts.push("Global Settings");
        }

        if (texts.length === 0) {
            return "All";
        }

        return texts.join(" & ");
    }, [value]);

    const [selection, setSelection] = useState<SelectionState>({ all: value.length === 0, selected: value });
    const canApply = selection.all || selection.selected.length > 0;

    const handleSelectionChange = (selection: SelectionState) => {
        setSelection(selection);
    };

    const handleApply = () => {
        onChange(selection.selected);
        togglePopover();
    };

    const handleCancel = () => {
        setSelection({ all: value.length === 0, selected: value });
        togglePopover();
    };

    return (
        <div className="relative">
            <Popover
                className="min-w-100"
                placement="bottom"
                offset={[0, 18]}
                visible={showPopover}
                onClickOutside={togglePopover}
            >
                <FilterMenuButton
                    isDropdown
                    showTooltipWithFullText
                    className="h-10 w-50"
                    text={buttonText}
                    onClick={togglePopover}
                />
                <Popover.Content>
                    <ProductSelector
                        className="rounded-b-none border-0"
                        groupBy="object"
                        value={selection}
                        collections={Object.values(ProductObject)}
                        exclude={[ProductGroupType.ExperienceGiftOffer]}
                        extendedList={{
                            prepend: [
                                {
                                    id: "global",
                                    name: "Global Settings",
                                },
                            ],
                        }}
                        onChange={handleSelectionChange}
                    />
                    <div className="flex flex-row-reverse gap-x-2 p-4">
                        <Button disabled={!canApply} size="medium" onClick={handleApply}>
                            Apply
                        </Button>
                        <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
                    </div>
                </Popover.Content>
            </Popover>
        </div>
    );
};
